import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Types } from '@grandvisionhq/graphql';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { useTrackPageView } from '@grandvisionhq/svc-tealium/events';
import { getDirectionsUrl, DateUtils } from '@grandvisionhq/utils';
import { useAddToCalendarDlEventDispatcher } from '../../data-layer/hooks/use-add-to-calendar-dl-event-dispatcher.js';
import '../../queries/book-appointment.js';
import '../../queries/get-calendar-availability.js';
import '../../queries/get-time-slots.js';
import '../../queries/get-store-offer-by-code.js';
import '../../queries/get-user.js';
import '../../queries/get-store-opening-times.js';
import '../../queries/get-availability-stores-nearby.js';
import 'date-fns';
import '@grandvisionhq/stores-v2';
import useStoreOfferByCode from '../../hooks/api/use-store-offer-by-code.js';
import { useAppointmentSlots } from '../../pages/BookAppointment/context/appointment-slots.js';
import { useAppointmentContext } from '../../pages/BookAppointment/context/AppointmentContext.js';
import { usePageData } from '../../pages/BookAppointment/context/PageDataContext.js';
import '../../pages/BookAppointment/queries/get-book-appointment-store.js';
import '../../pages/BookAppointment/queries/get-book-appointment-type.js';
import useVirtualAppointment from '../../pages/BookAppointment/hooks/use-virtual-appointment.js';
import { clearStoredAffiliateId } from '../../utils/affiliate-id-storage.js';
import AppointmentConfirmation from './AppointmentConfirmation.js';

const emptyArray = [];
const AppointmentConfirmationContainer = ()=>{
    const { getLabel , getFormattedLabel  } = useIntl();
    const [, { store , type , timeSlot , appointment: baseAppointment  }] = useAppointmentContext();
    const { googleMapsApiKey , storesUrl , opco , appointmentTypes , offerFallbackImageUrl  } = usePageData();
    const [{ isVirtualAppointmentType  }] = useVirtualAppointment();
    const [getStoreOfferByCode, storeOfferByCode] = useStoreOfferByCode({
        code: 0
    });
    const { Slots  } = useAppointmentSlots();
    const { isAabWithGroupedAppointmentTypesEnabled  } = useFeatureToggles();
    var ref4;
    const labels = React.useMemo(()=>{
        var ref1;
        return {
            heading: getFormattedLabel(`appointments.component.appointmentConfirmation.${(baseAppointment === null || baseAppointment === void 0 ? void 0 : baseAppointment.isReservation) ? 'reservationHeading' : 'heading'}`, {
                labelData: {
                    name: (ref4 = baseAppointment === null || baseAppointment === void 0 ? void 0 : (ref1 = baseAppointment.invitee) === null || ref1 === void 0 ? void 0 : ref1.firstName) !== null && ref4 !== void 0 ? ref4 : ''
                }
            }),
            subHeading: getLabel(`appointments.component.appointmentConfirmation.${(baseAppointment === null || baseAppointment === void 0 ? void 0 : baseAppointment.isReservation) ? 'reservationSubheading' : 'subHeading'}`),
            notification: getLabel(`appointments.component.appointmentConfirmation.${(baseAppointment === null || baseAppointment === void 0 ? void 0 : baseAppointment.preferredChannelOfCommunication) === Types.AppointmentCommunicationChannel.EMAIL ? 'emailNotification' : 'smsNotification'}`),
            addToCalendar: getLabel('appointments.component.appointmentConfirmation.addToCalendar'),
            appointment: getLabel('appointments.component.appointmentConfirmation.appointment'),
            where: getLabel('appointments.component.appointmentConfirmation.where'),
            who: getLabel('appointments.component.appointmentConfirmation.who'),
            getDirections: getLabel('appointments.component.appointmentConfirmation.getDirections'),
            cantMakeIt: getLabel('appointments.component.appointmentConfirmation.cantMakeIt'),
            cancelOrReschedule: getFormattedLabel('appointments.component.appointmentConfirmation.cancelOrReschedule', {
                parseHTML: true,
                parseLink: true
            }),
            exploreProducts: getLabel('appointments.component.appointmentConfirmation.exploreProducts'),
            tryInStoreProducts: getLabel('appointments.component.appointmentConfirmation.tryInStoreProducts.heading'),
            tryInStoreProductsDisclaimer: getLabel('appointments.component.appointmentConfirmation.tryInStoreProducts.disclaimer')
        };
    }, [
        baseAppointment,
        getLabel,
        getFormattedLabel
    ]);
    const appointment = React.useMemo(()=>baseAppointment && type ? swcHelpers.objectSpread({}, baseAppointment, {
            appointmentType: type
        }) : null
    , [
        baseAppointment,
        type
    ]);
    const directionsUrl = store ? getDirectionsUrl({
        streetName: store.streetName,
        town: store.town,
        opco
    }) : undefined;
    const appointmentConfirmationContent = React.useMemo(()=>{
        var ref, ref2, ref3;
        const appointmentContent = isAabWithGroupedAppointmentTypesEnabled ? (ref = appointmentTypes) === null || ref === void 0 ? void 0 : ref.reduce((acc, appointmentGroup)=>{
            appointmentGroup.appointmentTypes.forEach((entry)=>{
                if (entry.appointmentConfirmationContent) {
                    acc[entry.typeId] = entry.appointmentConfirmationContent;
                }
            });
            return acc;
        }, {}) : (ref2 = appointmentTypes) === null || ref2 === void 0 ? void 0 : ref2.find((content)=>{
            return content.typeId === (type === null || type === void 0 ? void 0 : type.id);
        });
        return (ref3 = appointmentContent) === null || ref3 === void 0 ? void 0 : ref3.appointmentConfirmationContent;
    }, [
        appointmentTypes,
        isAabWithGroupedAppointmentTypesEnabled,
        type === null || type === void 0 ? void 0 : type.id
    ]);
    const aabConfirmationPageView = React.useCallback(()=>new Promise((resolve)=>{
            if (!store || !type || !appointment || !timeSlot) {
                return;
            }
            resolve({
                id: 'VirtualPage-View',
                Page_Type: 'Confirmation',
                Page_Section1: 'Exam',
                Store_Search_StoreId: store.code.toString(),
                Exam_Fitting: type.name,
                Exam_Id: appointment.id,
                Exam_AppointmentDate: DateUtils.toLocalDate(timeSlot.from)
            });
        })
    , [
        store,
        type,
        appointment,
        timeSlot
    ]);
    useTrackPageView(aabConfirmationPageView);
    React.useEffect(()=>{
        if (!store) {
            return;
        }
        getStoreOfferByCode({
            variables: {
                code: Number(store.code)
            }
        });
    }, [
        getStoreOfferByCode,
        appointment,
        store
    ]);
    const { pushAddToCalendarDlEvent  } = useAddToCalendarDlEventDispatcher({
        appointmentCondition: 'new',
        type,
        store,
        isVirtualAppointment: isVirtualAppointmentType(type),
        timeSlot
    });
    React.useEffect(()=>{
        clearStoredAffiliateId();
    }, []);
    React.useEffect(()=>{
        var ref;
        (ref = Slots.clearTryInStoreStorage) === null || ref === void 0 ? void 0 : ref.call(Slots);
    }, [
        Slots
    ]);
    if (!store || !type || !timeSlot || !appointment) {
        return null;
    }
    var _data;
    return /*#__PURE__*/ React.createElement(AppointmentConfirmation, {
        labels: labels,
        store: store,
        offers: (_data = storeOfferByCode.data) !== null && _data !== void 0 ? _data : emptyArray,
        type: type,
        timeSlot: timeSlot,
        appointment: appointment,
        isVirtualAppointmentType: isVirtualAppointmentType(type),
        googleMapsApiKey: !isVirtualAppointmentType(type) ? googleMapsApiKey : undefined,
        storesUrl: !isVirtualAppointmentType(type) ? storesUrl : undefined,
        directionsUrl: directionsUrl,
        appointmentConfirmationContent: appointmentConfirmationContent,
        offerFallbackImageUrl: offerFallbackImageUrl,
        onICSButtonClick: pushAddToCalendarDlEvent,
        tryInStoreProducts: appointment.products
    });
};

export { AppointmentConfirmationContainer as default };
//# sourceMappingURL=AppointmentConfirmationContainer.js.map
